import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import toast from 'react-hot-toast';
import { staffLeaveAPI } from '../../services/api';
import { PageHeader } from '../Payroll/payrollUi';

export default function StaffLeaveInboxPage() {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');

  const inbox = useQuery(['staff-leave-inbox'], async () => {
    const res = await staffLeaveAPI.getInbox();
    return res.data.data;
  });

  const decide = useMutation(
    ({ id, decision, reason: rejectionReason }) =>
      staffLeaveAPI.decide(id, { decision, reason: rejectionReason }),
    {
      onSuccess: (res) => {
        qc.invalidateQueries(['staff-leave-inbox']);
        setRejecting(null);
        setReason('');
        const skipped = res?.data?.data?.timesheetSync?.skipped || [];
        if (skipped.length) {
          toast(skipped.map((row) => row.reason).filter(Boolean).join(' '), { duration: 8000 });
        }
        toast.success('Decision saved');
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Could not save the decision'),
    }
  );

  if (inbox.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (inbox.isError) {
    return <Alert severity="error">{inbox.error?.response?.data?.message || 'Could not load the inbox'}</Alert>;
  }

  const rows = inbox.data || [];

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Leave to approve"
        subtitle="Approve or send back what is waiting. People send these from My leave."
      />
      {rows.length === 0 && <Alert severity="info">Nothing is waiting.</Alert>}
      {rows.map((row) => (
        <Paper key={row.id} sx={{ p: 2 }}>
          <Typography variant="subtitle1">
            {row.ownerName} — {row.leaveTypeName}
          </Typography>
          <Typography variant="body2">
            {row.startDate} to {row.endDate} · {row.daySpan === 'half' ? 'Half day' : 'Full day'}
          </Typography>
          <Typography variant="body2">Days that count: {(row.countedDates || []).join(', ') || 'none'}</Typography>
          {row.balance && (
            <Typography variant="body2">
              {row.balance.remaining} days left now. After approval, {row.balance.remainingIfApproved} would be left.
              {row.balance.overdrawn ? ' The allowance is already used up, so extra days stay unpaid.' : ''}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="contained"
              disabled={decide.isLoading}
              onClick={() => decide.mutate({ id: row.id, decision: 'approved' })}
            >
              Approve
            </Button>
            <Button variant="outlined" onClick={() => { setRejecting(row); setReason(''); }}>
              Send back
            </Button>
          </Stack>
        </Paper>
      ))}

      <Dialog open={Boolean(rejecting)} onClose={() => setRejecting(null)} fullWidth maxWidth="sm">
        <DialogTitle>Send this leave back</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Tell them why"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)}>Back</Button>
          <Button
            variant="contained"
            disabled={!reason.trim() || decide.isLoading}
            onClick={() => decide.mutate({ id: rejecting.id, decision: 'rejected', reason })}
          >
            Send back
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
